import { useEffect, useMemo, useState } from "react";
import type { OwnerTerritory } from "../../domain/owner-territories";
import {
  batchBrandImages,
  batchTerritoryIdentities,
  brandImageContentUrl,
} from "../../lib/brand-image-api";
import { batchPublicPublications } from "../../lib/publication-api";
import { onTerritoryIdentityMutation } from "../../lib/territory-identity-invalidation";
import { onBrandImageMutation } from "../../lib/brand-image-invalidation";

/**
 * Per-territory presentation preview for the Dashboard list: the persistent
 * Territory Identity `display_name` (the real name) and its LOD0 Brand Image
 * thumbnail. Best-effort and non-authoritative — resolved via TWO batch calls
 * (identities by parcel, then brand images by identity) so there is no N+1.
 * On any failure the caller keeps the neutral fallback label/placeholder.
 */
export interface TerritoryPreview {
  identityId: string | null;
  displayName: string | null;
  thumbnailUrl: string | null;
  /** True when this territory has a CURRENT active publication (real state). */
  published: boolean;
}

export function useTerritoryPreviews(
  territories: OwnerTerritory[],
): Map<string, TerritoryPreview> {
  const [map, setMap] = useState<Map<string, TerritoryPreview>>(new Map());
  // Bumped by a confirmed Territory Identity mutation (name save / merge) OR a
  // Brand Image mutation (save / remove) so the list re-resolves the new
  // name/thumbnail immediately — same page, no reload.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    const offName = onTerritoryIdentityMutation(bump);
    const offImage = onBrandImageMutation(bump);
    return () => {
      offName();
      offImage();
    };
  }, []);

  const allUnitIds = useMemo(
    () => territories.flatMap((t) => t.unitIds),
    [territories],
  );
  const idsKey = useMemo(
    () => JSON.stringify([...allUnitIds].sort((a, b) => a - b)),
    [allUnitIds],
  );

  useEffect(() => {
    if (territories.length === 0) {
      setMap(new Map());
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const identities = await batchTerritoryIdentities(allUnitIds, controller.signal);
        // parcel id → identity (each owned parcel belongs to exactly one territory).
        const byParcel = new Map<number, (typeof identities)[number]>();
        for (const ident of identities) {
          for (const pid of ident.member_parcel_ids) byParcel.set(pid, ident);
        }

        const result = new Map<string, TerritoryPreview>();
        const identityIds: string[] = [];
        const keyToIdentity = new Map<string, string>();
        for (const t of territories) {
          let matched: (typeof identities)[number] | undefined;
          for (const pid of t.unitIds) {
            const m = byParcel.get(pid);
            if (m) { matched = m; break; }
          }
          if (matched) {
            keyToIdentity.set(t.key, matched.identity_id);
            identityIds.push(matched.identity_id);
            result.set(t.key, {
              identityId: matched.identity_id,
              displayName: matched.display_name,
              thumbnailUrl: null,
              published: false,
            });
          } else {
            result.set(t.key, { identityId: null, displayName: null, thumbnailUrl: null, published: false });
          }
        }

        const [images, pubs] = await Promise.all([
          batchBrandImages(identityIds, controller.signal),
          batchPublicPublications(identityIds, controller.signal),
        ]);
        const imageByIdentity = new Map(images.map((i) => [i.identity_id, i]));
        const publishedIds = new Set(pubs.map((p) => p.identity_id));
        for (const [key, identityId] of keyToIdentity) {
          const prev = result.get(key)!;
          const img = imageByIdentity.get(identityId);
          if (img) prev.thumbnailUrl = brandImageContentUrl(img.identity_id, img.revision);
          prev.published = publishedIds.has(identityId);
        }

        if (!controller.signal.aborted) setMap(new Map(result));
      } catch {
        // Previews are best-effort; keep neutral fallbacks on any error.
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, tick]);

  return map;
}
